// Iter101 — Admin: Team-Share Audit + Access Requests inbox.
// Two panels on one page so an admin can (1) see who's sharing what with
// whom + any exclusions, and (2) approve/reject Public Snapshot access
// requests without leaving the tab.
import { useEffect, useState } from "react";
import { useListControls, ListControlsBar } from "@/components/ListSearchSort";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, ShieldCheck, EyeOff, Check, X, Clock, Users, Lock } from "lucide-react";
import { toast } from "sonner";

export default function AdminTeamShareAudit() {
  const [overview, setOverview] = useState({ grantors: [], stats: {} });
  const [requests, setRequests] = useState([]);
  const reqCtl = useListControls(requests, { name: (r) => r.student_name, fields: (r) => [r.requester_name, r.student_name], initialSort: "default" });
  const [filter, setFilter] = useState("pending");   // pending | approved | rejected | all
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const q = filter === "all" ? "" : `?status_filter=${filter}`;
      const [o, r] = await Promise.all([
        api.get("/admin/team-share/overview"),
        api.get(`/admin/access-requests${q}`),
      ]);
      setOverview(o.data);
      setRequests(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load audit");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const decide = async (req, decision) => {
    if (!window.confirm(`${decision === "approve" ? "Approve" : "Reject"} access request from ${req.requester_name} for ${req.student_name}?`)) return;
    try {
      await api.post(`/admin/access-requests/${req.id}/decide`, { decision });
      toast.success(`Request ${decision === "approve" ? "approved" : "rejected"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Decision failed");
    }
  };

  const stats = overview.stats || {};

  return (
    <div className="min-h-screen bg-black text-white px-4 sm:px-8 py-6 space-y-5" data-testid="admin-team-share-audit">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-xs text-[#A3A3A3] hover:text-white">
        <ArrowLeft className="w-3.5 h-3.5" /> Admin console
      </Link>
      <div>
        <div className="label-mono text-[#CCFF00] mb-1">TEAM-SHARE AUDIT</div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Sharing & privacy oversight.</h1>
        <p className="text-sm text-[#A3A3A3] mt-2 max-w-3xl">
          Every coach-to-coach access grant, per-player exclusions, and pending Public Snapshot access requests in one view.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="audit-stats">
        <StatBox icon={Users} label="Coaches sharing" value={stats.grantor_count || 0} testid="stat-grantors" />
        <StatBox icon={Lock} label="Players with privacy" value={stats.students_with_privacy_activated || 0} testid="stat-privacy" />
        <StatBox icon={Clock} label="Pending requests" value={stats.pending_access_requests || 0} accent="#FF4500" testid="stat-pending" />
      </div>

      {/* Access requests */}
      <div className="card-surface p-5 space-y-3" data-testid="access-requests-panel">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="label-mono text-[#CCFF00] inline-flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" /> ACCESS REQUESTS
          </div>
          <div className="flex items-center gap-1" data-testid="access-request-filter">
            {["pending", "approved", "rejected", "all"].map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className="px-2.5 py-1 text-[11px] uppercase tracking-wider border transition-colors"
                style={filter === f
                  ? { background: "#CCFF00", color: "#0A0A0A", borderColor: "#CCFF00", fontWeight: 700 }
                  : { borderColor: "#262626", color: "#A3A3A3" }}
                data-testid={`access-request-filter-${f}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
        {loading && <div className="text-xs text-[#A3A3A3]">Loading…</div>}
        {!loading && requests.length === 0 && (
          <div className="text-xs text-[#525252] italic border border-dashed border-[#262626] p-3" data-testid="access-requests-empty">
            No {filter === "all" ? "" : filter} requests.
          </div>
        )}
        <div className="space-y-2">
          {requests.length > 0 && <ListControlsBar ctl={reqCtl} modes={["default", "az", "za"]} placeholder="Search coach or player…" testid="share-requests" />}
          {reqCtl.list.map((r) => (
            <div key={r.id} className="border border-[#262626] p-3 flex flex-wrap items-center gap-3" data-testid={`access-request-${r.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm">
                  <span className="text-white font-semibold">{r.requester_name}</span>
                  <span className="text-[#A3A3A3]"> wants access to </span>
                  <span className="text-[#CCFF00]">{r.student_name}</span>
                </div>
                {r.reason && <div className="text-[11px] text-[#A3A3A3] italic mt-0.5">&ldquo;{r.reason}&rdquo;</div>}
                <div className="text-[10px] text-[#525252] mt-1">
                  {new Date(r.created_at).toLocaleString()} · {r.requester_email}
                </div>
              </div>
              {r.status === "pending" ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => decide(r, "approve")}
                    className="text-xs px-3 py-1.5 bg-[#CCFF00] text-black font-semibold inline-flex items-center gap-1"
                    data-testid={`access-request-${r.id}-approve`}
                  >
                    <Check className="w-3.5 h-3.5" /> Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => decide(r, "reject")}
                    className="text-xs px-3 py-1.5 border border-[#FF4500] text-[#FF4500] inline-flex items-center gap-1"
                    data-testid={`access-request-${r.id}-reject`}
                  >
                    <X className="w-3.5 h-3.5" /> Reject
                  </button>
                </div>
              ) : (
                <div
                  className="label-mono text-[10px] px-2 py-1"
                  style={{
                    color: r.status === "approved" ? "#CCFF00" : "#FF4500",
                    borderColor: r.status === "approved" ? "#CCFF00" : "#FF4500",
                    border: "1px solid",
                  }}
                >
                  {r.status.toUpperCase()}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Team-share overview */}
      <div className="card-surface p-5 space-y-3" data-testid="team-share-overview-panel">
        <div className="label-mono text-[#CCFF00] inline-flex items-center gap-2">
          <Users className="w-3.5 h-3.5" /> ACTIVE GRANTS
        </div>
        {(overview.grantors || []).length === 0 ? (
          <div className="text-xs text-[#525252] italic border border-dashed border-[#262626] p-3" data-testid="grants-empty">
            No coach-to-coach sharing active.
          </div>
        ) : (
          <div className="space-y-3">
            {overview.grantors.map((g) => (
              <div key={g.coach_id} className="border border-[#262626] p-3" data-testid={`grantor-${g.coach_id}`}>
                <div className="text-sm mb-1">
                  <span className="text-white font-semibold">{g.name}</span>
                  <span className="text-[#A3A3A3]"> shares with </span>
                  <span className="text-[#CCFF00]">{g.grantee_ids.length}</span>
                  <span className="text-[#A3A3A3]"> coach{g.grantee_ids.length === 1 ? "" : "es"}</span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {g.grantee_ids.map((gr) => (
                    <span key={gr.coach_id} className="text-[11px] px-2 py-0.5 bg-[#0A0A0A] border border-[#262626] text-white">
                      {gr.name}
                    </span>
                  ))}
                </div>
                {g.excluded_students && g.excluded_students.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-[#262626]" data-testid={`grantor-${g.coach_id}-exclusions`}>
                    <div className="text-[10px] uppercase tracking-widest text-[#FF4500] mb-1 inline-flex items-center gap-1">
                      <EyeOff className="w-3 h-3" /> EXCLUDED PLAYERS
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {g.excluded_students.map((s) => (
                        <span key={s.student_id} className="text-[11px] px-2 py-0.5 bg-[#0A0A0A] border border-[#FF4500] text-[#FF4500]">
                          {s.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatBox({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="card-surface p-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="label-mono text-[#A3A3A3]">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color: accent || "#CCFF00" }} />}
      </div>
      <div className="text-2xl font-bold mt-1" style={accent ? { color: accent } : {}}>{value}</div>
    </div>
  );
}
